<script setup>
import { showPersonalized } from '#common/services/customerServices'
import ga4 from '#common/utils/ga4'

const { $auth, $axios, $config } = useNuxtApp()

// Reactive variables
const preloadHeroImage = ref('')
const screenWidth = ref(0)
const activeSection = ref([])
const isModalVisible = ref(true)
const isFirstLoad = ref(true)
const clubDisplay = ref({
  eligible: false,
  orderDate: '',
  creditAmount: 0,
})

// Fetch data
const { data: heroWidgetData, refresh: refreshHeroData } = await useFetch(
  '/api/homepage/hero-section',
)

const { data: heroIndexData, refresh: refreshHeroIndexData } = await useFetch(
  '/api/homepage/homepage-hero-index-data',
)
const userHasOrders = computed(() => heroIndexData.value?.user_orders ?? false)
const top10Wines = computed(() => heroIndexData.value?.top_10_wines ?? [])

// Computed properties
const loggedIn = computed(() => $auth.loggedIn)
const userKey = computed(() => $auth.user?.analytics_id || 1)
const isPersonalized = computed(() => showPersonalized($auth))
const dynamicHead = computed(() => ({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: preloadHeroImage.value,
      key: 'hero-preload',
    },
  ],
}))

// Methods
const updateHeroPreload = () => {
  if (import.meta.client) {
    screenWidth.value = window.innerWidth
    const cloudfrontBase =
      $config.public.cloudfrontBase || 'https://static.qa-wineaccess.com/'

    if (isPersonalized.value) {
      preloadHeroImage.value =
        screenWidth.value >= 768
          ? `${cloudfrontBase}images/home-personalized-large.webp`
          : `${cloudfrontBase}images/home-personalized-small.webp`
    } else if (screenWidth.value >= 1024) {
      preloadHeroImage.value = `${cloudfrontBase}images/home-large.webp`
    } else if (screenWidth.value >= 768) {
      preloadHeroImage.value = `${cloudfrontBase}images/home-medium.webp`
    } else {
      preloadHeroImage.value = `${cloudfrontBase}images/home-small.webp`
    }
  }
}

const progressiveLazyLoadSections = () => {
  const delay = 2000
  const sectionsQueue = [2, 3, 4, 5]
  sectionsQueue.forEach((section, index) => {
    setTimeout(
      () => {
        activeSection.value.push(section)
      },
      (index + 1) * delay,
    ) // Delay each section by 2 seconds
  })
}

const closeModal = () => {
  isModalVisible.value = false
}

// Lifecycle hooks
onMounted(async () => {
  if (import.meta.client) {
    screenWidth.value = window.innerWidth
    updateHeroPreload()
    progressiveLazyLoadSections()
    window.addEventListener('resize', updateHeroPreload)
  }

  try {
    const { data } = await $axios.get('api/customer/add_club_get_10_pct')
    clubDisplay.value = {
      eligible: !!data.eligible,
      orderDate: data.order_date || '',
      creditAmount: data.credit_amount || 0,
    }
    if (clubDisplay.value.eligible) {
      ga4.track('join_club_10_pct_popup_seen')
    }
  } catch (error) {
    console.error('Error fetching add club get 10pct data:', error)
  }
  isFirstLoad.value = false
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', updateHeroPreload)
  }
})

watch(loggedIn, (newStatus) => {
  if (!isFirstLoad.value && newStatus) {
    refreshHeroData()
    refreshHeroIndexData()
  }
})

useHead(dynamicHead)
</script>

<template>
  <div class="homepage">
    <div class="b24">
      <HeroSection :initial="heroWidgetData" />
      <PillScrollerSection v-if="loggedIn" />
      <UserOrderSection v-if="isPersonalized && userHasOrders" :key="userKey" />
    </div>
    <section v-if="top10Wines || activeSection.includes(2)">
      <Top10WinesSection v-if="top10Wines" :top10-wines-array="top10Wines" />
      <HomeLogobanSection />
      <HomeWineclubSection />
    </section>
    <section v-if="activeSection.includes(3)">
      <ShopCollectionsSection
        :key="userKey"
        :is-personalized="isPersonalized"
      />
    </section>
    <section v-if="activeSection.includes(4)">
      <HomeRestaurantsSection />
      <CustomerOpinionSection />
    </section>
    <section v-if="activeSection.includes(5)">
      <FeaturedCollectionsSection />
    </section>
    <JoinClub10pctModal
      v-if="clubDisplay.eligible && isModalVisible"
      :credit-amount="clubDisplay.creditAmount"
      :date="clubDisplay.orderDate"
      @close="closeModal"
    />
  </div>
</template>
